import {
  Avatar,
  Flex,
  Stack,
  HStack,
  Switch,
  Text,
  Spinner,
  Button,
  useDisclosure
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import StyledModal from '@components/general/modals/styled-modal.component';

import { useUserStore } from '@store/app.store';
import {
  useZoomUserSettings,
  usePatchZoomUserSettings,
  useDownloadZoomUserRecordings
} from '@services/query-services/lambda.query-service';
import { useInvalidateQueries, useStyledToast } from '@util/hooks';

export default function ZoomPersonPreview({ user }) {
  const toast = useStyledToast();
  const navigate = useNavigate();
  const tenant = useUserStore((state) => state?.tenant);
  const invalidateQueries = useInvalidateQueries();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: zoomUserSettings, isLoading } = useZoomUserSettings(
    tenant?.recall_config?.zoom_oauth_credential_id,
    user?.id
  );
  const { mutateAsync: patchUserSettings } = usePatchZoomUserSettings();
  const { mutateAsync: downloadUserRecordings, isLoading: isDownloading } =
    useDownloadZoomUserRecordings();

  const handleSwitchChange = async (event) => {
    await patchUserSettings(
      {
        zoom_oauth_credential_id: tenant?.recall_config?.zoom_oauth_credential_id,
        user_id: user?.id,
        payload: {
          recording: {
            local_recording: event.target.checked
          }
        }
      },
      {
        onSuccess: () => {
          toast({
            title: 'User updated successfully',
            status: 'success',
            duration: 9000,
            isClosable: true
          });
          invalidateQueries({
            model: 'lambda',
            queryKey: [
              'GET',
              'zoom/user-settings',
              undefined,
              {
                zoom_oauth_credential_id: tenant?.recall_config?.zoom_oauth_credential_id,
                user_id: user?.id
              }
            ]
          });
        }
      }
    );
  };

  const handleDownloadRecordings = async () => {
    try {
      await downloadUserRecordings({
        zoom_oauth_credential_id: tenant?.recall_config?.zoom_oauth_credential_id,
        user_id: user?.id,
        tenant_id: tenant?.id
      });

      toast({
        title: 'Recording import initiated',
        description: 'User recordings download has been started successfully',
        status: 'success',
        duration: 9000,
        isClosable: true
      });

      onClose();

      navigate('/settings/recordings-admin');
    } catch (error) {
      toast({
        title: 'Import failed',
        description: 'Failed to initiate recordings download. Please try again.',
        status: 'error',
        duration: 9000,
        isClosable: true
      });
    }
  };

  return (
    <Flex
      gap={2.5}
      size='sm'
      p={3}
      border='reskin'
      borderRadius='xl'
    >
      <Avatar
        src={user?.pic_url}
        size='sm'
      />
      <Stack
        gap='6px'
        align='flex-start'
      >
        <Text
          color='brandPrimary.primary'
          fontWeight={600}
          letterSpacing='tight'
          fontSize='sm'
          lineHeight='16px'
          textOverflow='ellipsis'
        >
          {user?.display_name}
        </Text>
        <Flex
          color='surface.onSurfaceVariant'
          fontSize='xs'
          lineHeight='14px'
          fontWeight={500}
          w='full'
        >
          <Text as='span'>{user?.email}</Text>
        </Flex>
      </Stack>
      <Flex
        w='100%'
        align='flex-start'
        justifyContent='flex-end'
        alignSelf='flex-end'
      >
        {isLoading ? (
          <Spinner
            size='sm'
            data-testid='loading-spinner'
          />
        ) : (
          <Stack>
            <HStack>
              <Switch
                isChecked={zoomUserSettings?.recording?.local_recording}
                onChange={handleSwitchChange}
                p={2}
                data-testid='recording-switch'
              />
              <Stack align='flex-start'>
                <Text
                  fontSize='sm'
                  variant='page-header'
                >
                  Local Recording
                </Text>
                <Text
                  fontWeight={500}
                  color='surface.onSurfaceVariant'
                  fontSize='xs'
                >
                  FORA requires local recording be enabled
                </Text>
              </Stack>
            </HStack>
            <Button
              variant='reskinPrimary'
              onClick={() => onOpen()}
              size='sm'
              data-testid='open-modal-button'
            >
              Import Recordings
            </Button>
          </Stack>
        )}
      </Flex>

      <StyledModal
        disclosure={{ isOpen, onOpen, onClose }}
        title='Import Recordings'
        modalContentProps={{ w: '440px' }}
      >
        <Flex
          flexDir='column'
          gap={6}
          p={4}
          mt={2}
        >
          <Text>
            This will initiate a download of this users recordings into FORA from the last 30 days.
          </Text>

          <Flex
            gap={3}
            justify='flex-end'
          >
            <Button
              variant='ghost'
              onClick={onClose}
              data-testid='cancel-button'
            >
              Cancel
            </Button>
            <Button
              variant='reskinPrimary'
              onClick={handleDownloadRecordings}
              isLoading={isDownloading}
              data-testid='import-recordings-button'
            >
              Import Recordings
            </Button>
          </Flex>
        </Flex>
      </StyledModal>
    </Flex>
  );
}
